import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { BottomNav } from "@/components/BottomNav";
import { MbCard } from "@/components/MbCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { Send, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { gu } from "date-fns/locale";
import { AppFooter } from "@/components/AppFooter";

interface Message {
  id: string;
  body: string;
  direction: "in" | "out";
  created_at: string;
}

export default function Chat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [sending, setSending] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logo, strings } = useLanguage();

  useEffect(() => {
    // Set up auth listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (!session) {
          navigate("/auth");
        }
      }
    );

    // Then check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session) {
        navigate("/auth");
      } else {
        if (session.user) {
          checkAuth(session.user);
        }
        fetchMessages();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Update profileId when user changes
  useEffect(() => {
    if (user) {
      checkAuth(user);
    }
  }, [user]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!profileId) return;

    const channel = supabase
      .channel('user-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `user_id=eq.${profileId}`
        },
        (payload) => {
          console.log('New message received:', payload);
          setMessages((current) => [...current, payload.new as Message]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profileId]);

  const checkAuth = async (currentUser: User) => {
    if (!currentUser) return;

    // Get profile ID
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("auth_user_id", currentUser.id)
      .single();

    if (profile) {
      setProfileId(profile.id);
    }
  };

  const fetchMessages = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("auth_user_id", user.id)
        .single();

      if (!profile) return;

      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("user_id", profile.id)
        .order("created_at", { ascending: true });

      if (error) throw error;

      setMessages(data || []);
    } catch (error: any) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !profileId) return;

    setSending(true);
    try {
      const { error } = await supabase.from("messages").insert({
        user_id: profileId,
        body: newMessage.trim(),
        direction: "in",
      });

      if (error) throw error;

      setNewMessage("");
      fetchMessages();

      toast({
        title: strings.messageSent,
        description: strings.motabhaiWillReply,
      });
    } catch (error: any) {
      toast({
        title: strings.error,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="animate-pulse text-muted-foreground">{strings.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] pb-20">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="દમયંતી બાગ" className="h-10" />
            <h1 className="text-xl font-bold text-primary">{strings.chatWith}</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-4 space-y-4 mb-24">
        {messages.length > 0 ? (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.direction === "in" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-4 ${
                  message.direction === "in"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                <p
                  className={`text-xs mt-2 ${
                    message.direction === "in"
                      ? "text-primary-foreground/70"
                      : "text-muted-foreground"
                  }`}
                >
                  {new Date(message.created_at).toLocaleTimeString("gu-IN", {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          ))
        ) : (
          <MbCard className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">{strings.noMessages}</h2>
            <p className="text-muted-foreground">{strings.askMotabhai}</p>
          </MbCard>
        )}
      </div>

      {/* Message Input - Fixed at bottom */}
      <div className="fixed bottom-16 left-0 right-0 bg-card border-t p-4 z-40">
        <form onSubmit={handleSend} className="container mx-auto flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={strings.typeMessage}
            disabled={sending}
            className="flex-1"
          />
          <Button
            type="submit"
            variant="default"
            size="icon"
            disabled={sending || !newMessage.trim()}
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>

      <div className="pb-16">
        <AppFooter />
      </div>

      <BottomNav />
    </div>
  );
}
